// FiltersDrawer.tsx
import {
    Box,
    Typography,
    Divider,
    Accordion,
    AccordionSummary,
    AccordionDetails,
    IconButton,
    Tabs,
    Tab,
    Tooltip,
    Paper,
} from '@mui/material';
import { styled, alpha } from '@mui/material/styles';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ClearAllIcon from '@mui/icons-material/ClearAll';
import FilterAltOutlinedIcon from '@mui/icons-material/FilterAltOutlined';
import BusinessIcon from '@mui/icons-material/Business';
import StorefrontIcon from '@mui/icons-material/Storefront';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import MemoryIcon from '@mui/icons-material/Memory';
import AddIcon from '@mui/icons-material/Add';
import FilterListIcon from '@mui/icons-material/FilterList';
import { useSelector } from 'react-redux';
import type { RoleUtils } from '@/types/permissions';
import { OrgFilters } from '@components/OrgFilters';
import { Selection } from './orgNavTypes';
import type { FilterInput } from './OrgNav';
import type { SystemFilter } from '@/types/systems';
import type { SystemAssigned } from '@consts/systems';
import { SystemTableFilters } from './Tables/SystemTable/SystemTableFilters';
import { SearchBar } from '@components/SearchBar';
import BadgeIcon from '@mui/icons-material/Badge';

interface FiltersDrawerProps {
    /** Controls visibility of the filter panel */
    open: boolean;
    /** Currently selected entity tab */
    selectedTab: Selection;
    /** Callback to change the selected tab */
    setSelectedTab: (tab: Selection) => void;
    /** Org-level filters */
    orgFilters: FilterInput;
    setOrgFilter: (k: keyof FilterInput, v: string, one?: boolean) => void;
    /** System-level filters */
    systemFilters: SystemFilter;
    setSystemFilter: (k: keyof SystemFilter, v: string, one?: boolean) => void;
    assigned: SystemAssigned;
    setAssigned: (v: SystemAssigned) => void;
    /** Search term for system table */
    searchTerm: string;
    setSearchTerm?: (v: string) => void;
    /** Clear helpers */
    onClearOrg: () => void;
    onClearSystem: () => void;
    /** Callback invoked when user clicks "+" to create new entity */
    onCreate?: (type: Selection) => void;
}

// Professionally styled vertical tab reflecting analytics-dashboard design
const StyledTab = styled(Tab)(({ theme }) => ({
    alignItems: 'center',
    justifyContent: 'flex-start',
    textTransform: 'none',
    minHeight: 44,
    paddingLeft: theme.spacing(3), // offset for left indicator
    paddingRight: theme.spacing(2),
    borderRadius: theme.shape.borderRadius,
    gap: theme.spacing(1),
    fontWeight: 500,
    color: theme.palette.text.secondary,
    position: 'relative',
    transition: theme.transitions.create(['background-color', 'color'], {
        duration: theme.transitions.duration.short,
    }),
    '&.Mui-selected': {
        backgroundColor: alpha(theme.palette.primary.main, 0.08),
        color: theme.palette.text.primary,
        fontWeight: 600,
    },
    '&.Mui-selected .MuiSvgIcon-root': {
        color: theme.palette.primary.main,
    },
    '&.Mui-selected::before': {
        content: '""',
        position: 'absolute',
        left: 0,
        top: 4,
        bottom: 4,
        width: 3,
        borderRadius: 3,
        backgroundColor: theme.palette.primary.main,
    },
    '&:hover': {
        backgroundColor: theme.palette.action.hover,
        color: theme.palette.text.primary,
    },
}));

export const FiltersDrawer = ({
    open,
    selectedTab,
    setSelectedTab,
    orgFilters,
    setOrgFilter,
    systemFilters,
    setSystemFilter,
    assigned,
    setAssigned,
    searchTerm,
    setSearchTerm,
    onClearOrg,
    onClearSystem,
    onCreate,
}: FiltersDrawerProps) => {
    if (!open) return null;

    // Current user's role utilities for permission checks
    const role = useSelector((state: any) => state.auth.Roles.role as RoleUtils);

    const canCreateCompany = role.isAdmin && role.stores.edit;
    const canCreateReseller = role.stores.edit && role.resellers.fullAccess;
    const canCreateLocation = role.stores.edit && role.locations.fullAccess;

    const width = 240;

    return (
        <Paper
            elevation={1}
            sx={{
                width,
                flexShrink: 0,
                boxSizing: 'border-box',
                p: 2,
                borderRight: '1px solid #e0e0e0',
                height: 'calc(100dvh - 172px)',
                overflowY: 'auto',
            }}>
            {/* Header */}
            <Box display='flex' alignItems='center' gap={1} mb={2}>
                <FilterAltOutlinedIcon color='primary' sx={{ fontSize: 20, mt: 0.05 }} />
                <Typography variant='subtitle1' sx={{ fontWeight: 700, letterSpacing: '0.5px', color: 'text.primary' }}>
                    Filters
                </Typography>
            </Box>

            {/* Tab Selection */}
            <Tabs
                value={selectedTab}
                orientation='vertical'
                variant='scrollable'
                sx={{ mb: 1, '& .MuiTabs-indicator': { display: 'none' } }}>
                {/* Companies Tab */}
                <StyledTab
                    icon={<BusinessIcon fontSize='small' />}
                    iconPosition='start'
                    label={
                        <Box display='flex' alignItems='center' width='100%' justifyContent='space-between'>
                            <span>Companies</span>
                            {canCreateCompany && (
                                <IconButton
                                    size='small'
                                    sx={{ p: 0.25 }}
                                    onClick={(e) => {
                                        e.stopPropagation();
                                        onCreate?.(Selection.Company);
                                    }}>
                                    <AddIcon fontSize='small' sx={{ color: '#718096 !important' }} />
                                </IconButton>
                            )}
                        </Box>
                    }
                    value={Selection.Company}
                    onClick={() => setSelectedTab(Selection.Company)}
                />

                {/* Resellers Tab */}
                <StyledTab
                    icon={<StorefrontIcon fontSize='small' />}
                    iconPosition='start'
                    label={
                        <Box display='flex' alignItems='center' width='100%' justifyContent='space-between'>
                            <span>Resellers</span>
                            {canCreateReseller && (
                                <IconButton
                                    size='small'
                                    sx={{ p: 0.25 }}
                                    onClick={(e) => {
                                        e.stopPropagation();
                                        onCreate?.(Selection.Reseller);
                                    }}>
                                    <AddIcon fontSize='small' sx={{ color: '#718096 !important' }} />
                                </IconButton>
                            )}
                        </Box>
                    }
                    value={Selection.Reseller}
                    onClick={() => setSelectedTab(Selection.Reseller)}
                />

                {/* Locations Tab */}
                <StyledTab
                    icon={<LocationOnIcon fontSize='small' />}
                    iconPosition='start'
                    label={
                        <Box display='flex' alignItems='center' width='100%' justifyContent='space-between'>
                            <span>Locations</span>
                            {canCreateLocation && (
                                <IconButton
                                    size='small'
                                    sx={{ p: 0.25 }}
                                    onClick={(e) => {
                                        e.stopPropagation();
                                        onCreate?.(Selection.Location);
                                    }}>
                                    <AddIcon fontSize='small' sx={{ color: '#718096 !important' }} />
                                </IconButton>
                            )}
                        </Box>
                    }
                    value={Selection.Location}
                    onClick={() => setSelectedTab(Selection.Location)}
                />
                {/* Employees Tab */}
                <StyledTab
                    icon={<BadgeIcon fontSize='small' />}
                    iconPosition='start'
                    label={
                        <Box display='flex' alignItems='center' width='100%' justifyContent='space-between'>
                            <span>Employees</span>
                            <IconButton
                                size='small'
                                sx={{ p: 0.25 }}
                                onClick={(e) => {
                                    e.stopPropagation();
                                    onCreate?.(Selection.Employee);
                                }}>
                                <AddIcon fontSize='small' sx={{ color: '#718096 !important' }} />
                            </IconButton>
                        </Box>
                    }
                    value={Selection.Employee}
                    onClick={() => setSelectedTab(Selection.Employee)}
                />
                {/* Systems Tab */}
                <StyledTab
                    icon={<MemoryIcon fontSize='small' />}
                    iconPosition='start'
                    label='Systems'
                    value={Selection.System}
                    onClick={() => setSelectedTab(Selection.System)}
                />
            </Tabs>

            <Divider sx={{ my: 1 }} />

            {/* ORG FILTERS */}
            <Accordion defaultExpanded elevation={0} sx={{ boxShadow: 'none' }}>
                <AccordionSummary expandIcon={<ExpandMoreIcon />} sx={{ px: 0 }}>
                    <Typography fontWeight={500}>Organization</Typography>
                </AccordionSummary>
                <AccordionDetails sx={{ px: 0 }}>
                    <Box display='flex' flexDirection='column' gap={1}>
                        <OrgFilters
                            orgFilters={orgFilters}
                            setOrgFilters={setOrgFilter as any}
                            handleClear={onClearOrg}
                            showLocations={
                                selectedTab === Selection.Location ||
                                selectedTab === Selection.System ||
                                selectedTab === Selection.Employee
                            }
                            showResellers={selectedTab !== Selection.Company}
                        />
                    </Box>
                </AccordionDetails>
            </Accordion>

            {/* SYSTEM FILTERS */}
            {selectedTab === Selection.System && (
                <Accordion defaultExpanded elevation={0} sx={{ boxShadow: 'none' }}>
                    <AccordionSummary expandIcon={<ExpandMoreIcon />} sx={{ px: 0 }}>
                        <Typography fontWeight={500}>Systems</Typography>
                    </AccordionSummary>
                    <AccordionDetails sx={{ px: 0 }}>
                        <Box display='flex' flexDirection='column' gap={1}>
                            <SearchBar width={208} value={searchTerm} onSearch={setSearchTerm!} />
                            <SystemTableFilters
                                filters={systemFilters}
                                setFilter={setSystemFilter as any}
                                assigned={assigned}
                                setAssigned={setAssigned}
                                searchTerm={searchTerm}
                            />
                        </Box>
                    </AccordionDetails>
                </Accordion>
            )}

            {/* EMPLOYEE FILTERS */}
            {selectedTab === Selection.Employee && (
                <Accordion defaultExpanded elevation={0} sx={{ boxShadow: 'none' }}>
                    <AccordionSummary expandIcon={<ExpandMoreIcon />} sx={{ px: 0 }}>
                        <Typography fontWeight={500}>Employees</Typography>
                    </AccordionSummary>
                    <AccordionDetails sx={{ px: 0 }}>
                        <SearchBar width={208} value={searchTerm} onSearch={setSearchTerm!} />
                    </AccordionDetails>
                </Accordion>
            )}
        </Paper>
    );
};
